/**
 * Claro, oscuro, o lo que diga el sistema.
 *
 * La paleta oscura estaba escrita entera en `styles/tokens.css`, bajo
 * `:root[data-theme="dark"]` — y **ningún archivo del proyecto escribía ese
 * atributo**. Estaba terminada y apagada. Lo único que faltaba es esto.
 *
 * ## Por qué se guarda en el navegador y no en el servidor
 *
 * Es una preferencia del dispositivo, no de la persona. La misma persona quiere
 * oscuro en la notebook de noche y claro en el celular al sol, y una fila por
 * usuario en la base le daría el mismo tema en los dos. Además evita una consulta
 * antes de poder pintar la pantalla.
 *
 * ## Por qué oscuro es el valor por defecto
 *
 * El rediseño es oscuro: es el tema diseñado y el que se validó pantalla por
 * pantalla. El claro sigue disponible, y «sistema» también, pero hay que
 * elegirlos.
 *
 * Como «sin preferencia» ahora significa oscuro, «sistema» se guarda explícito
 * en vez de borrar la clave. Quien había elegido claro u oscuro lo conserva.
 */

export type Theme = "light" | "dark" | "system";

const KEY = "clara-theme";

export const DEFAULT_THEME: Theme = "dark";

export const THEMES: readonly { value: Theme; label: string }[] = [
  { value: "dark", label: "Oscuro" },
  { value: "light", label: "Claro" },
  { value: "system", label: "Según el sistema" },
];

export function storedTheme(): Theme {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === "light" || saved === "dark" || saved === "system" ? saved : DEFAULT_THEME;
  } catch {
    // Modo privado, o `localStorage` bloqueado. No poder recordar la
    // preferencia no puede impedir que la aplicación arranque.
    return DEFAULT_THEME;
  }
}

/** Qué tema toca de verdad, resolviendo «sistema». */
export function resolve(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Pinta el tema. Se llama **antes de montar React** para que no haya un
 * parpadeo blanco antes del oscuro.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = resolve(theme);
}

export function saveTheme(theme: Theme): void {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Igual que arriba: se aplica en esta sesión aunque no se pueda recordar.
  }
  applyTheme(theme);
}

/**
 * Sigue los cambios del sistema mientras la preferencia sea «sistema».
 *
 * Sin esto, alguien que tiene el cambio automático al atardecer ve que su
 * sistema se pone oscuro y esta pestaña se queda clara hasta que la recargue.
 */
export function watchSystem(current: () => Theme): () => void {
  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  if (!media) return () => {};

  const onChange = () => {
    if (current() === "system") applyTheme("system");
  };
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
